import { Box, Container, Typography, Stack, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useLanguage } from '../contexts/LanguageContext';
import { veriLobiData } from '../data/productsMockData';
import ProductFeatureCard from '../components/ProductFeatureCard';
import ProductBenefitCard from '../components/ProductBenefitCard';
import SEOHead from '../components/SEOHead';

const HeroSection = styled(Box)(({ theme }) => ({
  padding: '160px 0 80px',
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)'
    : 'linear-gradient(180deg, #FFFFFF 0%, #F9FAFB 100%)',
  position: 'relative',
}));

const ContentSection = styled(Box)(({ theme }) => ({
  padding: '80px 0',
  background: theme.palette.mode === 'dark'
    ? '#0F172A'
    : '#FFFFFF',
}));

const HeroImage = styled('img')({
  width: '100%',
  height: '400px',
  objectFit: 'cover',
  borderRadius: '24px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
});

const CTAButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  color: '#FFFFFF',
  padding: '14px 32px',
  borderRadius: '14px',
  fontWeight: 700,
  fontSize: '1rem',
  textTransform: 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 12px 32px rgba(16, 185, 129, 0.4)'
      : '0 12px 32px rgba(37, 99, 235, 0.35)',
  },
}));

export default function VeriLobiProduct() {
  const { language, t } = useLanguage();

  const hero = veriLobiData.hero;
  const features = veriLobiData.features;
  const benefits = veriLobiData.benefits;
  const useCases = veriLobiData.useCases;

  return (
    <>
      <SEOHead 
        title={`${language === 'tr' ? hero.titleTR : hero.titleEN} - Teknolobi`}
        description={language === 'tr' ? hero.descriptionTR : hero.descriptionEN}
      />
      
      <Box component="main" role="main">
        <HeroSection>
          <Container maxWidth="lg">
            <Button
              component={Link}
              to="/"
              startIcon={<ArrowBackIcon />}
              sx={{ mb: 4, color: 'text.primary' }}
            >
              {language === 'tr' ? 'Ana Sayfa' : 'Home'}
            </Button>

            <Stack spacing={6}>
              <Stack spacing={3} alignItems="center" textAlign="center">
                <Typography
                  variant="h1"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '2.5rem', md: '4rem' },
                    background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {language === 'tr' ? hero.titleTR : hero.titleEN}
                </Typography>
                <Typography
                  variant="h4"
                  color="text.secondary"
                  sx={{ maxWidth: 800, fontWeight: 600 }}
                >
                  {language === 'tr' ? hero.taglineTR : hero.taglineEN}
                </Typography>
                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{ maxWidth: 900, lineHeight: 1.8 }}
                >
                  {language === 'tr' ? hero.descriptionTR : hero.descriptionEN}
                </Typography>
                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                  <CTAButton>{t('verilobi.cta.demo')}</CTAButton>
                  <CTAButton variant="outlined">{t('verilobi.cta.contact')}</CTAButton>
                </Stack>
              </Stack>

              <HeroImage src={hero.heroImage} alt={language === 'tr' ? hero.titleTR : hero.titleEN} />
            </Stack>
          </Container>
        </HeroSection>

        {/* Features Section */}
        <ContentSection>
          <Container maxWidth="lg">
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2rem', md: '3rem' },
                mb: 6,
                textAlign: 'center',
                background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              {t('product.features.title')}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(2, 1fr)',
                  lg: 'repeat(3, 1fr)',
                },
                gap: 3,
              }}
            >
              {features.map((feature, index) => (
                <ProductFeatureCard
                  key={index}
                  icon={feature.icon}
                  title={language === 'tr' ? feature.titleTR : feature.titleEN}
                  description={language === 'tr' ? feature.descriptionTR : feature.descriptionEN}
                />
              ))}
            </Box>
          </Container>
        </ContentSection>

        {/* Benefits Section */}
        <ContentSection sx={{ background: (theme) => theme.palette.mode === 'dark' ? '#1E293B' : '#F9FAFB' }}>
          <Container maxWidth="lg">
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2rem', md: '3rem' },
                mb: 6,
                textAlign: 'center',
                background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              {t('product.benefits.title')}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(2, 1fr)',
                },
                gap: 3,
              }}
            >
              {benefits.map((benefit, index) => (
                <ProductBenefitCard
                  key={index}
                  title={language === 'tr' ? benefit.titleTR : benefit.titleEN}
                  description={language === 'tr' ? benefit.descriptionTR : benefit.descriptionEN}
                />
              ))}
            </Box>
          </Container>
        </ContentSection>

        {/* Use Cases Section */}
        <ContentSection>
          <Container maxWidth="lg">
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2rem', md: '3rem' },
                mb: 6,
                textAlign: 'center',
                background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              {t('product.usecases.title')}
            </Typography>
            <Stack spacing={3}>
              {useCases.map((useCase, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 4,
                    borderRadius: '20px',
                    background: (theme) => theme.palette.mode === 'dark'
                      ? 'rgba(30, 41, 59, 0.6)'
                      : 'rgba(255, 255, 255, 0.9)',
                    border: (theme) => `2px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
                  }}
                >
                  <Typography variant="h5" fontWeight={700} mb={1}>
                    {language === 'tr' ? useCase.titleTR : useCase.titleEN}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" lineHeight={1.8}>
                    {language === 'tr' ? useCase.descriptionTR : useCase.descriptionEN}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Container>
        </ContentSection>

        {/* CTA Section */}
        <ContentSection sx={{ background: (theme) => theme.palette.mode === 'dark' ? '#1E293B' : '#F9FAFB' }}>
          <Container maxWidth="md">
            <Stack spacing={3} alignItems="center" textAlign="center">
              <Typography
                variant="h3"
                fontWeight={800}
                sx={{
                  background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {t('product.cta.title')}
              </Typography>
              <Typography variant="h6" color="text.secondary">
                {t('product.cta.subtitle')}
              </Typography>
              <Stack direction="row" spacing={2}>
                <CTAButton size="large">{t('verilobi.cta.demo')}</CTAButton>
                <CTAButton size="large" variant="outlined">{t('verilobi.cta.contact')}</CTAButton>
              </Stack>
            </Stack>
          </Container>
        </ContentSection>
      </Box>
    </>
  );
}